import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addOrRemoveItem } from '../helpers/utils';
import { apiFetch, filterPageSortUrl } from '../api';

export function createTableSlice({ name, url, filters = {}, sortOrder = 'asc', orderBy = null }) {
  const initialState = {
    rowIds: [],
    rowsById: {},
    orderBy,
    sortOrder,
    selectedRows: [], //  prev: rowsSelected
    totalItems: 0,
    page: 0,
    limit: 50,
    filters,
    filterTypes: {},
  };

  function mapEnumFilters(filters, filterTypes) {
    const mappedFilters = {};
    for (const [filterName, filterValue] of Object.entries(filters)) {
      const enumType = filterTypes[filterName];
      if (enumType) {
        for (const [enumName, enumValue] of Object.entries(enumType)) {
          if (enumName.toLowerCase().includes(filterValue.toLowerCase())) {
            mappedFilters[filterName] = enumValue;
            break;
          }
        }
      } else {
        mappedFilters[filterName] = filterValue;
      }
    }
    return mappedFilters;
  }

  const fetchRows = createAsyncThunk(`${name}/fetchRows`, async (_, { getState }) => {
    const {
      [name]: { limit, page, filters, filterTypes, orderBy, sortOrder },
    } = getState();

    let mappedFilters = mapEnumFilters(filters, filterTypes);

    const data = await apiFetch(
      filterPageSortUrl(url, { limit, page, orderBy, sortOrder, filters: mappedFilters })
    );
    return data;
  });

  let setFilterTimer = null;

  const setFilter = createAsyncThunk(
    `${name}/setFilter`,
    async ({ filterName, value, type }, { dispatch }) => {
      await dispatch(tableSlice.actions.updateFilter({ filterName, type, value }));
      await dispatch(tableSlice.actions.updateTableState({ page: 0 }));

      if (setFilterTimer) {
        clearTimeout(setFilterTimer);
      }
      setFilterTimer = setTimeout(() => {
        dispatch(fetchRows());
      }, 300);
    }
  );

  const setTableState = createAsyncThunk(
    `${name}/setTableState`,
    async (tableState, { dispatch }) => {
      await dispatch(tableSlice.actions.updateTableState(tableState));
      await dispatch(fetchRows());
    }
  );

  const tableSlice = createSlice({
    name,
    initialState,
    reducers: {
      updateTableState(state, action) {
        return { ...state, ...action.payload };
      },
      updateFilter(state, action) {
        const { filterName, value, type } = action.payload;
        state.filters[filterName] = value;
        if (value == '') {
          delete state.filters[filterName];
        }
        if (type) {
          state.filterTypes[filterName] = type;
        }
      },
      updateRow(state, action) {
        const row = action.payload;
        state.rowsById[row.id] = row;
      },
      toggleRowSelected: {
        reducer(state, action) {
          state.selectedRows = addOrRemoveItem(state.selectedRows, action.payload).sort();
        },
      },
      setAllSelected: {
        reducer(state) {
          state.selectedRows = state.rowIds;
        },
      },
      clearSelection: {
        reducer(state) {
          state.selectedRows = [];
        },
      },
    },
    extraReducers: (builder) => {
      builder.addCase(fetchRows.fulfilled, (state, action) => {
        const rows = action.payload[action.payload.name];
        rows.forEach((row) => {
          state.rowsById[row.id] = row;
        });
        state.rowIds = rows.map((row) => row.id);
        state.totalItems = action.payload.total_items;
      });
    },
  });

  const selectRowIds = (state) => state[name].rowIds;
  const selectTableState = (state) => state[name];
  const selectFilters = (state) => state[name].filters;
  const selectRowsById = (state) => state[name].rowsById;
  const selectSelectedRows = (state) => state[name].selectedRows;

  return {
    name: name,
    reducer: tableSlice.reducer,
    actions: {
      ...tableSlice.actions,
      fetchRows,
      setFilter,
      setTableState,
    },
    selectors: {
      selectRowIds,
      selectTableState,
      selectFilters,
      selectRowsById,
      selectSelectedRows,
    },
  };
}

export function createTabSlice({ name, tabs }) {
  const sliceTabs = tabs.map((tab, idx) => {
    return {
      index: idx,
      props: {},
      ...tab,
    };
  });
  const tabSlice = createSlice({
    name,
    initialState: {
      tabs: sliceTabs,
      activeTab: sliceTabs[0],
    },
    reducers: {
      setTab(state, action) {
        if (action.payload.id) {
          state.activeTab = state.tabs.find((tab) => tab.id == action.payload.id);
        } else {
          state.activeTab = state.tabs.find((tab) => tab.label == action.payload.label);
        }
        state.props = action.payload.props || {};
      },
    },
  });

  const selectActiveTab = (state) => state[name].activeTab;
  const selectTabs = (state) => state[name].tabs;
  const selectProps = (state) => state[name].props;

  return {
    name: name,
    reducer: tabSlice.reducer,
    actions: {
      ...tabSlice.actions,
    },
    selectors: {
      selectActiveTab,
      selectTabs,
      selectProps,
    },
  };
}
